import React, {
  useContext, useState, KeyboardEvent, useEffect
} from 'react'
import { GridItem } from '@zuto/core/src/grid'
import { RadioButton } from '@zuto/atoms'
import { useCallbackOnce } from '@zuto/utilities'
import QuestionComponentProps from '../../../Questions/question-component-props'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import AppFormQuestion from '../../AppFormQuestion'
import withQuestion from '../../HOC/withQuestion'
import withFade from '../../HOC/withFade'
import VehicleTypeOptions from './VehicleTypeOptions'
import {
  trackMultipleInputChoice,
  determineRadioVariant,
  handlePrevButtonClick,
  questionFontStyle
} from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import { PrefillReviewPageApplicationFormContext } from '../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import ToggleButtonGroup from '@zuto/atoms/src/Mui/ToogleButtonGroup/ToggleButtonGroup'

const VehicleTypeQuestion = ({
  existingAnswers,
  disablePrevious,
  updateFollowingPrefillStep
}: QuestionComponentProps): JSX.Element => {
  const { setQuestionName, hasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const appFormContext = useContext(ApplicationFormContext)
  const { featureSwitches } = appFormContext
  const {
    prominentColourScheme,
    removeContinueCtaV1,
    reviewPageV3,
    questionCopyChangeV1,
    msmThemeV1
  } = featureSwitches ?? {}
  const answerInitValue = existingAnswers || (!removeContinueCtaV1 ? 'car' : '')
  const [answer, setAnswer] = useState(answerInitValue)

  const [nextButtonCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()

  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('vehicleType')
    }
  }, [])

  const updateAnswer = (value: string) => {
    setAnswer(value)
    if (removeContinueCtaV1) {
      appFormContext.dispatch({
        type: 'QUESTION_ANSWERED',
        name: QuestionKey.VEHICLE_TYPE,
        value
      })
    }
  }

  const nextQuestion = () => {
    trackMultipleInputChoice({ label: `vehicleType+${answer}` })
    appFormContext.dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.VEHICLE_TYPE,
      value: answer
    })
    // Used for Navigation on Prefill Review Page
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, false, true)
      }
    }
  }

  const previousQuestion = () => {
    handlePrevButtonClick(appFormContext.dispatch, {
      name: QuestionKey.VEHICLE_TYPE,
      value: answer
    })
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(false, false, true)
      }
    }
  }

  const handleEnterPress = (e: KeyboardEvent) => {
    if (e.code === 'Enter' && answer) {
      nextButtonCb(nextQuestion)
    }
  }

  const renderOptions = () => {
    if (msmThemeV1) {
      return (
        <ToggleButtonGroup
          options={VehicleTypeOptions}
          id="vehicle-type"
          defaultState={answer}
          valueChanged={(value) => updateAnswer(value.toString())}
        />
      )
    }
    return (
      <RadioButton
        options={VehicleTypeOptions}
        id="vehicle-type"
        direction="vertical"
        mb={3}
        mt={3}
        valueChanged={(value) => updateAnswer(value.toString())}
        onKeyPress={handleEnterPress}
        defaultState={answer}
        variant={determineRadioVariant({
          prominentColourScheme, 
          smallRadiosSideBySide: false, 
          sideBySideEnabled: true
        })}
        alignment="center"
        removeContinueCta={removeContinueCtaV1}
      />
    )
  }

  return (
    <>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']} minHeight="174px">
        <AppFormQuestion
          questionKey={QuestionKey.VEHICLE_TYPE}
          mt={3}
          textStyle={questionFontStyle}
        >
          {questionCopyChangeV1 ? 'What would you like a finance quote for?' : 'What do you want to finance?'}
        </AppFormQuestion>
        {renderOptions()}
      </GridItem>
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextQuestion)}
        previousHandler={() => previousButtonCb(previousQuestion)}
        disableNext={!answer}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
        hideNext={removeContinueCtaV1}
      />
    </>
  )
}

export default withFade(withQuestion(VehicleTypeQuestion))
